function safeJsonParse(value) {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function isObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasCollectionKeys(value) {
  return (
    Array.isArray(value?.content) ||
    Array.isArray(value?.items) ||
    Array.isArray(value?.result) ||
    Array.isArray(value?.data)
  );
}

function hasPaginationKeys(value) {
  return [
    "total",
    "totalElements",
    "count",
    "page",
    "pageIndex",
    "size",
    "pageSize",
    "pages",
    "message",
  ].some((key) => value?.[key] !== undefined && value?.[key] !== null);
}

function getNestedWrapperFromArray(items) {
  if (!Array.isArray(items) || items.length !== 1) return null;

  const firstItem = items[0];
  if (!isObject(firstItem)) return null;

  return hasCollectionKeys(firstItem) || hasPaginationKeys(firstItem)
    ? firstItem
    : null;
}

function getCollectionContainer(payload) {
  if (!payload) return null;
  if (Array.isArray(payload)) return null;

  if (Array.isArray(payload.content)) return payload;

  if (Array.isArray(payload.items)) {
    const nested = getNestedWrapperFromArray(payload.items);
    return nested ? getCollectionContainer(nested) ?? nested : payload;
  }

  if (Array.isArray(payload.result)) {
    const nested = getNestedWrapperFromArray(payload.result);
    return nested ? getCollectionContainer(nested) ?? nested : payload;
  }

  if (isObject(payload.data)) {
    const nested = getCollectionContainer(payload.data);
    if (nested) return nested;
  }

  if (isObject(payload.result)) {
    const nested = getCollectionContainer(payload.result);
    if (nested) return nested;
  }

  if (hasPaginationKeys(payload)) return payload;

  return null;
}

function getCollectionItemsFromPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  if (Array.isArray(payload.content)) return payload.content;

  if (Array.isArray(payload.items)) {
    const nested = getNestedWrapperFromArray(payload.items);
    return nested ? getCollectionItemsFromPayload(nested) : payload.items;
  }

  if (Array.isArray(payload.result)) {
    const nested = getNestedWrapperFromArray(payload.result);
    return nested ? getCollectionItemsFromPayload(nested) : payload.result;
  }

  if (Array.isArray(payload.data)) return payload.data;

  if (isObject(payload.data)) {
    const nestedItems = getCollectionItemsFromPayload(payload.data);
    if (nestedItems.length > 0) return nestedItems;
  }

  if (isObject(payload.result)) {
    const nestedItems = getCollectionItemsFromPayload(payload.result);
    if (nestedItems.length > 0) return nestedItems;
  }

  return [];
}

function pickFirstDefined(source, keys = []) {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) {
      return source[key];
    }
  }

  return undefined;
}

export function parseApiPayload(response) {
  const raw = response?.data ?? response;

  if (isObject(raw?.body)) return raw.body;

  const parsedBody = safeJsonParse(raw?.body);
  if (parsedBody !== raw?.body) return parsedBody;

  const parsedRaw = safeJsonParse(raw);
  return parsedRaw;
}

export function extractCollectionItems(response) {
  const payload = parseApiPayload(response);
  return getCollectionItemsFromPayload(payload);
}

export function extractPaginatedCollection(response, fallbackLength = 0) {
  const payload = parseApiPayload(response);
  const items = extractCollectionItems(payload);
  const container = getCollectionContainer(payload) ?? payload ?? {};

  const totalRaw =
    pickFirstDefined(container, ["totalElements", "total", "count"]) ??
    pickFirstDefined(payload, ["totalElements", "total", "count"]);

  return {
    content: items,
    totalElements: Number.isFinite(Number(totalRaw))
      ? Number(totalRaw)
      : fallbackLength,
    page:
      pickFirstDefined(container, ["page", "pageIndex"]) ??
      pickFirstDefined(payload, ["page", "pageIndex"]),
    size:
      pickFirstDefined(container, ["size", "pageSize"]) ??
      pickFirstDefined(payload, ["size", "pageSize"]),
    pages: pickFirstDefined(container, ["pages"]) ?? pickFirstDefined(payload, ["pages"]),
    message:
      pickFirstDefined(container, ["message"]) ??
      pickFirstDefined(payload, ["message"]),
  };
}
